import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Stepper } from '@/components/eventos/Stepper';
import { C } from '@/design-system/dna';
import { brl } from '../shared';

const fill = (arr, n) => Array.from({ length: n }, (_, i) => arr?.[i] || '');

/** Quantidades de extras do aluno + nomes de convidados / placas (opcionais). Total em tempo real. */
export function ExtrasDialog({ part, data, eventId, setData, onClose }) {
  const [q, setQ] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!part) return;
    const m = {};
    data.produtos.forEach((p) => { const l = part.extras_lines.find((x) => x.product_id === p.id); m[p.id] = { qty: l?.qty || 0, convidados: l?.convidados || [], veiculos: l?.veiculos || [] }; });
    setQ(m);
  }, [part, data.produtos]);
  if (!part) return null;
  const prods = data.produtos.filter((p) => p.ativo || q[p.id]?.qty);
  const extras = prods.reduce((s, p) => s + (q[p.id]?.qty || 0) * p.valor, 0);
  const total = part.valor_evento + extras;
  const set = (id, k, v) => setQ((x) => ({ ...x, [id]: { ...x[id], [k]: v } }));
  const save = async () => {
    setBusy(true);
    try {
      const items = Object.entries(q).map(([product_id, v]) => ({ product_id, qty: v.qty, convidados: fill(v.convidados, v.qty), veiculos: fill(v.veiculos, v.qty) }));
      const { data: d } = await api.put(`/eventos/${eventId}/participantes/${part.student_id}/extras`, { items }); setData(d); toast.success('Extras atualizados.'); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar extras.'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!part} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="evento-extras-dialog">
        <DialogHeader><DialogTitle>Extras · {part.student_name}</DialogTitle><DialogDescription>{part.class_name} · {part.responsible_name || 'responsável não informado'}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {prods.map((p) => {
            const v = q[p.id] || { qty: 0 };
            const max = p.restantes != null ? p.restantes + (part.extras_lines.find((x) => x.product_id === p.id)?.qty || 0) : null;
            return (
              <div key={p.id} className="rounded-2xl border p-3" style={{ borderColor: C.line }} data-testid={`evento-extras-item-${p.id}`}>
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1"><b className="text-[13.5px]" style={{ color: C.ink }}>{p.nome}</b><p className="text-[11.5px]" style={{ color: C.muted }}>{brl(p.valor)} cada{p.restantes != null ? ` · ${p.restantes} restante(s)` : ''}{p.limite_familia ? ` · até ${p.limite_familia}/família` : ''}{!p.ativo ? ' · inativo' : ''}</p></div>
                  <Stepper value={v.qty} max={p.ativo ? max : v.qty} onChange={(n) => set(p.id, 'qty', n)} testid={`evento-extras-qtd-${p.id}`} />
                  <b className="w-24 text-right tabular-nums">{brl(v.qty * p.valor)}</b>
                </div>
                {v.qty > 0 && (p.tipo === 'convite' || p.tipo === 'estacionamento') && (
                  <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                    {fill(v[p.tipo === 'convite' ? 'convidados' : 'veiculos'], v.qty).map((n, i) => (
                      <Input key={i} value={n} onChange={(e) => { const k = p.tipo === 'convite' ? 'convidados' : 'veiculos'; const arr = fill(v[k], v.qty); arr[i] = e.target.value; set(p.id, k, arr); }}
                        placeholder={p.tipo === 'convite' ? `Nome do convidado ${i + 1} (opcional)` : `Veículo / placa ${i + 1} (opcional)`} className="h-9" data-testid={`evento-extras-${p.tipo}-${p.id}-${i}`} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {!prods.length && <p className="text-[12.5px]" style={{ color: C.muted }}>Cadastre produtos extras na seção “Produtos extras”.</p>}
          <div className="grid grid-cols-3 gap-2 rounded-2xl p-3 text-center" style={{ background: C.paper }} data-testid="evento-extras-total">
            <div><p className="text-[11px]" style={{ color: C.muted }}>Evento</p><b>{brl(part.valor_evento)}</b></div>
            <div><p className="text-[11px]" style={{ color: C.muted }}>Extras</p><b>{brl(extras)}</b></div>
            <div><p className="text-[11px]" style={{ color: C.muted }}>Total da família</p><b style={{ color: C.purple }} data-testid="evento-extras-total-valor">{brl(total)}</b></div>
          </div>
          {part.pago > 0 && <p className="text-[11.5px]" style={{ color: C.muted }}>Já pago: {brl(part.pago)} · novo saldo {brl(Math.max(0, total - part.pago))}</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={save} disabled={busy} data-testid="evento-extras-salvar">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
